import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { getModules } from "@/services/admin/modules.service";
import { Module } from "@/services/admin/modules.interfaces";
import { useAppToast } from "@/hooks/useAppToast";
import { Input } from "@/components/ui/input";

/**
 * List page for module test pools. Admin selects a module to open its pool edit page.
 * Spec: add-module-test-pool-dedicated-admin-frontend
 */
const ModuleTestsListPage: React.FC = () => {
  const [modules, setModules] = useState<Module[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const toast = useAppToast();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getModules()
      .then((data) => {
        if (!cancelled) setModules(data ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Falha ao carregar módulos");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [toast]);

  const filtered = searchQuery.trim()
    ? modules.filter(
        (m) =>
          m.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.internalName?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : modules;

  return (
    <AdminPageLayout title="Testes de módulo">
      <div className="space-y-6">
        <p className="text-muted-foreground">
          Selecione um módulo para configurar o pool de exercícios do teste desse módulo (criar, editar, remover e reordenar).
        </p>
        <div>
          <label htmlFor="module-search" className="block text-sm font-medium mb-1">
            Buscar módulo
          </label>
          <Input
            id="module-search"
            type="search"
            placeholder="Nome, título ou nome interno..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-md"
          />
        </div>
        {loading && (
          <div className="py-8 text-muted-foreground">Carregando módulos...</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="py-8 text-muted-foreground">
            {searchQuery.trim() ? "Nenhum módulo encontrado com o filtro." : "Nenhum módulo cadastrado."}
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((module) => (
              <li key={module.id}>
                <Link
                  to={`/admin/educational/module-tests/${module.id}`}
                  className="block border rounded-lg p-4 hover:border-primary hover:bg-muted/50 transition-colors"
                >
                  <h3 className="font-medium">{module.name}</h3>
                  {module.title && (
                    <p className="text-sm text-muted-foreground mt-0.5">{module.title}</p>
                  )}
                  <span className="text-xs text-muted-foreground mt-2 inline-block">
                    Configurar pool do teste →
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminPageLayout>
  );
};

export default ModuleTestsListPage;
